import React, { useMemo } from 'react';
import * as Styled from './styled';
import { DealType, getBuildingClassKey, BuildingClass } from 'utils/entities';
import EstimatedPrice from './estimated-price';
import { useLocale } from 'locale';
import { AgentTags } from 'components/cards/listing/UniversalCard/AgentTags';
import OfferDetails from './offer-details';
import { createOfferTypesByMarketPlace, createOfferProps } from './offer-details/helper';
import useScrollToPriceEstimation from 'hooks/useScrollToPriceEstimation';
import { formatPrimaryAndSecondaryAddress } from 'utils/addressFormatter';

interface MainInfoProps {
  currentPrice: number;
  primaryAddress: string;
  secondaryAddress: string;
  pricePerSizeUnit?: number;
  dealType: string;
  floor?: string | number;
  floors?: string | number;
  beds?: number;
  area?: number;
  buildingClass: BuildingClass;
  priceDropped?: number;
  isCommercial?: boolean;
  sendPriceEstimationClickEvent?: () => void;
}

export interface AgentProps {
  isExclusive?: boolean;
  isAgent?: boolean;
}


export type WrapperMainInfoProps = MainInfoProps & AgentProps;


export const MainInfo: React.FC<WrapperMainInfoProps> = React.memo(({
  currentPrice,
  primaryAddress,
  secondaryAddress,
  pricePerSizeUnit,
  dealType,
  beds,
  floor,
  area,
  isAgent,
  isExclusive,
  priceDropped,
  floors,
  buildingClass,
  isCommercial,
  sendPriceEstimationClickEvent,
}) => {
  const { t, formatMoney, formatArea } = useLocale();

  const offers = useMemo(() => createOfferTypesByMarketPlace(isCommercial), [ isCommercial ]);
  const showEstimatedPrice = dealType === DealType.Buy && !isCommercial;

  const handleClick = useScrollToPriceEstimation(sendPriceEstimationClickEvent);

  return (
    <Styled.Root>
      <Styled.PriceInfoWrapper>
        {priceDropped && showEstimatedPrice ? <Styled.PreviousPrice data-auto="price">{formatMoney(priceDropped)}</Styled.PreviousPrice> : null}
        <Styled.PriceInfo>
          <Styled.CurrentPriceText data-auto="current-price">{formatMoney(currentPrice)}</Styled.CurrentPriceText>
          {showEstimatedPrice ? <EstimatedPrice handleScroll={handleClick} /> : priceDropped && <Styled.PreviousPrice data-auto="price">{formatMoney(priceDropped)}</Styled.PreviousPrice>}
        </Styled.PriceInfo>
        <Styled.PrimaryAddressText data-auto="primary_address_text">{formatPrimaryAndSecondaryAddress(primaryAddress, secondaryAddress)}</Styled.PrimaryAddressText>
        <Styled.SecondaryAddress data-auto="secondary_address">
          {t('unitPage.dealTypeLabel', { buildingClass: getBuildingClassKey(buildingClass), dealType, isCommercial })}
          {isAgent ? <AgentTags isExclusive={isExclusive} /> : null}
        </Styled.SecondaryAddress>
      </Styled.PriceInfoWrapper>
      <Styled.OfferDetailsContainer>
        {offers.map((offerType) => {
          const offerProps = createOfferProps(offerType, { beds, t, area, floor, floors, isCommercial, pricePerSizeUnit, formatMoney, formatArea, dealType });
          return offerProps ? <OfferDetails key={offerProps.locator} {...offerProps} /> : null;
        })}
      </Styled.OfferDetailsContainer>
    </Styled.Root>
  );
});
